import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import { formatBDT } from "utils/formatPrice"
import { formatReadableDate } from "utils/formatDate"

const buildCustomerRows = preOrder => {
  const customerPdf = preOrder?.pdf_content?.customer_info || {}
  const customer = preOrder?.customer || {}

  return [
    { label: "Name", value: customerPdf.name || customer.name || "-" },
    { label: "ID", value: customer.cuid || "-" },
    { label: "Mobile", value: customerPdf.mobile || customer.mobile || "-" },
    { label: "Email", value: customerPdf.email || customer.email || "-" },
    {
      label: "Address",
      value: customerPdf.address || customer.full_address || "-",
    },
  ]
}

const buildVehicleRows = preOrder => {
  const vehiclePdf = preOrder?.pdf_content?.vehicle_info || {}
  const vehicle = preOrder?.vehicle || null

  return [
    { label: "Name", value: vehiclePdf.name || "-" },
    { label: "Stock ID", value: vehiclePdf.stock_id || vehicle?.stock_id || "-" },
    {
      label: "Chassis",
      value: vehiclePdf.full_chassis || vehicle?.full_chassis || "-",
    },
    {
      label: "Engine",
      value: vehiclePdf.full_engine || vehicle?.full_engine || "-",
    },
    { label: "Year", value: vehiclePdf.year_model || "-" },
    { label: "Color", value: vehiclePdf.color || "-" },
    { label: "CC", value: vehiclePdf.cc || "-" },
    { label: "Fuel", value: vehiclePdf.fuel || "-" },
  ]
}

const InfoRows = ({ rows }) => (
  <div className="border rounded p-3">
    {rows.map(row => (
      <div className="d-flex justify-content-between" key={row.label}>
        <span className="text-muted">{row.label}</span>
        <strong>{row.value}</strong>
      </div>
    ))}
  </div>
)

const GenerateMoneyReceiptModal = ({
  isOpen,
  toggle,
  preOrder,
  onSubmit,
  isSubmitting = false,
}) => {
  const today = new Date().toISOString().split("T")[0]
  const customerRows = buildCustomerRows(preOrder)
  const vehicleRows = buildVehicleRows(preOrder)
  const payments = preOrder?.payments ?? []

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      car_price: preOrder?.car_price ?? "",
      registration_cost: preOrder?.registration_cost ?? "",
      issued_date: today,
      notes: "",
    },
    validationSchema: Yup.object({
      car_price: Yup.number()
        .typeError("Car price must be numeric")
        .integer("Car price must be a whole number")
        .min(0, "Car price cannot be negative")
        .required("Car price is required"),
      registration_cost: Yup.number()
        .typeError("Registration cost must be numeric")
        .integer("Registration cost must be a whole number")
        .min(0, "Registration cost cannot be negative")
        .nullable(),
      issued_date: Yup.string().required("Issued date is required"),
      notes: Yup.string().nullable().max(1000, "Notes are too long"),
    }),
    onSubmit: async values => {
      await onSubmit({
        id: preOrder?.id,
        car_price: Number(values.car_price),
        registration_cost: values.registration_cost === "" ? 0 : Number(values.registration_cost),
        issued_date: values.issued_date,
        notes: values.notes?.trim() || null,
      })

      formik.resetForm()
      toggle()
    },
  })

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <Form onSubmit={formik.handleSubmit}>
        <ModalHeader toggle={toggle}>
          Generate Money Receipt — {preOrder?.poid ?? ""}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Label className="form-label fw-semibold">Customer Info</Label>
              <InfoRows rows={customerRows} />
            </Col>
            <Col md={6}>
              <Label className="form-label fw-semibold">Vehicle Info</Label>
              <InfoRows rows={vehicleRows} />
            </Col>
          </Row>

          <Label className="form-label fw-semibold">Payment Summary</Label>
          {payments.length ? (
            <div className="table-responsive mb-2">
              <Table size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => (
                    <tr key={payment.id}>
                      <td>
                        {formatReadableDate(payment?.received_date, "MMM d, y")}
                      </td>
                      <td>{payment?.payment_method?.name ?? "-"}</td>
                      <td className="text-end">{formatBDT(payment?.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Total Deposit</th>
                    <th className="text-end">
                      {formatBDT(preOrder?.deposit_amount)}
                    </th>
                  </tr>
                </tfoot>
              </Table>
            </div>
          ) : (
            <div className="border rounded p-3 mb-2 text-muted small">
              No deposit payments recorded yet for this booking.
            </div>
          )}

          <div className="small text-muted mb-3">
            These deposit payments will be carried over onto the receipt —
            no new payment is created here.
          </div>

          <Row className="g-3">
            <Col md={6}>
              <Label className="form-label">Car Price (BDT)</Label>
              <Input
                type="number"
                name="car_price"
                value={formik.values.car_price}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.car_price && formik.errors.car_price)}
              />
              <FormFeedback>{formik.errors.car_price}</FormFeedback>
            </Col>

            <Col md={6}>
              <Label className="form-label">Registration Cost (BDT)</Label>
              <Input
                type="number"
                name="registration_cost"
                value={formik.values.registration_cost}
                onChange={formik.handleChange}
                invalid={
                  !!(
                    formik.touched.registration_cost &&
                    formik.errors.registration_cost
                  )
                }
              />
              <FormFeedback>{formik.errors.registration_cost}</FormFeedback>
            </Col>

            <Col md={12}>
              <Label className="form-label">Issued Date</Label>
              <Input
                type="date"
                name="issued_date"
                value={formik.values.issued_date}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.issued_date && formik.errors.issued_date)}
              />
              <FormFeedback>{formik.errors.issued_date}</FormFeedback>
            </Col>

            <Col md={12}>
              <Label className="form-label">Notes</Label>
              <Input
                type="textarea"
                name="notes"
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
                invalid={!!(formik.touched.notes && formik.errors.notes)}
              />
              <FormFeedback>{formik.errors.notes}</FormFeedback>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {isSubmitting ? "Generating..." : "Generate Money Receipt"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default GenerateMoneyReceiptModal
